import { ReactNode, memo } from "react";
import type { Moment } from "moment";
import { makeStyles } from "tss-react/mui";
import { MiChip, MiIcon } from "@miplatform/components";
import { mdiPlus } from "@mdi/js";
import type { SxProps } from "@mui/material";
import { Box, Divider, Tooltip, Typography } from "@mui/material";
import { AssignmentsColumnSection } from "./AssignmentsColumnSection";

interface AssignmentsColumnProps {
  isEmpty?: boolean;
  isDisabled?: boolean;
  isSelected?: boolean;
  isOrdersColumn?: boolean;
  title?: string;
  subtitle?: string;
  count?: number | string;
  additionalCount?: number | string;
  ordersCount?: number | string;
  width?: number;
  minHeight?: number;
  sections?: {
    date: string | Moment;
    count?: number;
    children?: ReactNode;
  }[];
  children?: ReactNode;
  sx?: SxProps;
  headerSx?: SxProps;
  headerChildren?: ReactNode;
  onClick?: () => void;
  onHeaderClick?: () => void;
}

export const AssignmentsColumn = memo(
  ({
    isEmpty,
    isOrdersColumn,
    isDisabled,
    isSelected,
    title,
    subtitle,
    count,
    additionalCount,
    ordersCount,
    width = 195,
    minHeight,
    sections,
    children,
    sx,
    onClick,
    onHeaderClick,
  }: AssignmentsColumnProps) => {
    const titleMaxWidth = (() => {
      const titleWidth = subtitle ? width - 105 : width - 55;

      return additionalCount ? titleWidth - 40 : titleWidth;
    })();
    const { theme, classes } = useStyles({
      isDisabled,
      isSelected,
      minHeight,
      width,
      titleMaxWidth,
      isClickEnabled: Boolean(onClick),
    });

    const renderSections = () =>
      sections?.map((section) => (
        <AssignmentsColumnSection
          key={section.date.toString()}
          date={section.date}
          count={section.count}
          height={`${Math.floor(100 / (sections.length || 1))}%`}
        >
          {section.children}
        </AssignmentsColumnSection>
      ));

    return (
      <Box
        className={classes.root}
        sx={{ maxWidth: width, minWidth: width, ...sx }}
        onClick={onClick}
      >
        {title && (
          <Box
            className={classes.header}
            {...(!isDisabled && {
              onClick: onHeaderClick,
              sx: {
                cursor: !isDisabled && onHeaderClick ? "pointer" : "default",
              },
            })}
          >
            <Box className={classes.row}>
              <Box display="flex" alignItems="center" gap={1}>
                <Tooltip title={title}>
                  <Typography noWrap className={classes.title}>
                    {title}
                  </Typography>
                </Tooltip>
                {subtitle && (
                  <>
                    <Divider
                      orientation="vertical"
                      sx={{
                        backgroundColor: theme.palette.grey.accent,
                        height: 14,
                      }}
                    />
                    <Tooltip title={subtitle}>
                      <Typography noWrap className={classes.subtitle}>
                        {subtitle}
                      </Typography>
                    </Tooltip>
                  </>
                )}
              </Box>
              <MiChip
                label={count || 0}
                size="small"
                color="error"
                sx={{ height: 14, fontSize: 11 }}
              />
              {Boolean(additionalCount) && (
                <>
                  <MiIcon
                    path={mdiPlus}
                    color={theme.palette.info.main}
                    size={0.66}
                  />
                  <MiChip
                    label={additionalCount}
                    size="small"
                    color="info"
                    sx={{
                      height: 14,
                      fontSize: 11,
                    }}
                  />
                </>
              )}
            </Box>
            {ordersCount !== undefined && (
              <Box className={classes.count}>{`${ordersCount} Order${
                ordersCount === 1 ? "" : "s"
              }`}</Box>
            )}
          </Box>
        )}
        {children && !isEmpty && (
          <AssignmentsColumnSection>{children}</AssignmentsColumnSection>
        )}
        {sections && renderSections()}
        {((!sections && !children) || isEmpty) && (
          <AssignmentsColumnSection isOrdersColumn={isOrdersColumn} />
        )}
      </Box>
    );
  }
);

const useStyles = makeStyles<{
  isDisabled?: boolean;
  isSelected?: boolean;
  isClickEnabled: boolean;
  minHeight?: number;
  width: number;
  titleMaxWidth: number;
}>()(
  (
    theme,
    { isDisabled, isSelected, minHeight, width, titleMaxWidth, isClickEnabled }
  ) => ({
    root: {
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(1),
      padding: theme.spacing(1, 2),
      borderRadius: theme.spacing(1),
      backgroundColor: theme.palette.grey.light,
      border: `1px solid ${
        isSelected ? theme.palette.primary.main : theme.palette.grey.accent
      }`,
      height: "100%",
      minHeight: minHeight || 260,
      overflow: "hidden",
      ...(isClickEnabled && {
        cursor: "pointer",
      }),
      ...(isDisabled && {
        opacity: 0.2,
      }),
    },
    header: {
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(1),
    },
    row: {
      display: "flex",
      gap: theme.spacing(1),
      alignItems: "center",
    },
    count: {
      fontSize: theme.typography.pxToRem(11),
      backgroundColor: theme.palette.grey.mediumLight,
      border: `1px solid ${theme.palette.grey.darkAccent}`,
      width: "fit-content",
      padding: theme.spacing(0, 1),
    },
    title: {
      fontSize: theme.fontSize.medium,
      fontWeight: theme.fontWeight.semiBold,
      overflow: "hidden",
      whiteSpace: "nowrap",
      textOverflow: "ellipsis",
      maxWidth: titleMaxWidth,
    },
    subtitle: {
      fontSize: theme.fontSize.xsmall,
      fontWeight: theme.fontWeight.regular,
      color: theme.palette.grey.medium,
      overflow: "hidden",
      whiteSpace: "nowrap",
      textOverflow: "ellipsis",
      maxWidth: 50,
    },
  })
);
